import type { ReactElement } from "react"

import { buildCostSectionView } from "@/util/cost-section-view"
import {
  buildPetGlanceStats,
  buildPetGlanceTimeframe,
} from "@/util/pet-at-a-glance"
import type {
  CostSummaryBand,
  MarketplaceCostSummary,
} from "@/util/cost-summary"

type PetTransportAtAGlanceProps = {
  // Bare location for the heading — "Phoenix", "Arizona". The per-stat scope
  // lines use the qualified label the cost data actually belongs to, which is
  // not always this one (a city can fall back to its state's band).
  headingLabel: string
  // "Los Angeles, CA" on city pages; omitted on state pages. Same props the
  // cost section takes, so both views are built from one set of inputs.
  cityLabel?: string
  stateLabel: string
  costBand: CostSummaryBand | null
  marketplaceCost: MarketplaceCostSummary | null
  costDataWindow: { from: string; to: string } | null
  shipmentsPosted: number | null
}

// Compact marketplace summary placed directly under the hero and its quote CTA
// on the pet city and state landing pages (MAR-78), so the proprietary local
// numbers are the first thing after the primary conversion path instead of
// sitting below several screens of informational copy.
//
// Three constraints shape the markup:
//   - It sits ABOVE the fold-adjacent content, so it stays a bounded
//     summary: a handful of short rows, no chart, no embed. The full band cards,
//     trend chart and calculator stay in PetCostSection further down.
//   - The data window captions the block instead of sitting in a cell: it
//     qualifies the cost and distance figures rather than being a metric of its
//     own. It is a <p>, not a styled <span>, so the .md alternate keeps it on
//     its own line under the heading.
//   - Each statistic must survive being read outside this grid (by a screen
//     reader, a markdown alternate, or an AI retrieval pass), so it is a real
//     <dl> of self-contained term/value pairs rather than styled <div>s — the
//     label names the metric, and the detail line names the scope the figure
//     belongs to.
//
// Renders nothing when the stat list comes back short; see buildPetGlanceStats.
export default function PetTransportAtAGlance({
  headingLabel,
  cityLabel,
  stateLabel,
  costBand,
  marketplaceCost,
  costDataWindow,
  shipmentsPosted,
}: PetTransportAtAGlanceProps): ReactElement | null {
  const costView = buildCostSectionView({
    cityLabel,
    stateLabel,
    costBand,
    marketplaceCost,
  })
  const stats = buildPetGlanceStats({
    costView,
    shipmentsPosted,
    locationLabel: cityLabel ?? stateLabel,
  })
  const timeframe = buildPetGlanceTimeframe(costDataWindow, costView)

  if (stats.length === 0) {
    return null
  }

  return (
    <section
      id="pet-transport-at-a-glance"
      aria-labelledby="pet-transport-at-a-glance-heading"
      className="max-w-[1289px] mx-auto px-4 md:px-8 mt-8 md:mt-12"
    >
      <div className="rounded-lg border border-gray-200 bg-blue16 px-5 py-6 md:px-8 md:py-7">
        <div className="mb-5">
          <h2
            id="pet-transport-at-a-glance-heading"
            className="mb-0 font-semibold font-montserrat text-2xl md:text-[28px] leading-8 md:leading-9 text-blue7"
          >
            {headingLabel} Pet Transport at a Glance
          </h2>
          {timeframe && (
            <p
              data-testid="pet-glance-timeframe"
              className="mt-2 mb-0 text-sm text-gray-600"
            >
              {timeframe}
            </p>
          )}
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
          {stats.map((stat) => (
            <div key={stat.key}>
              <dt className="text-sm text-gray-600">{stat.label}</dt>
              <dd className="m-0">
                <div className="text-xl font-semibold font-montserrat leading-7">
                  {stat.value}
                </div>
                {stat.detail && (
                  <div className="mt-1 text-sm text-gray-600">
                    {stat.detail}
                  </div>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  )
}
